import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Search } from 'lucide-react';
import { axiosInstance } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import PersonPicker from './PersonPicker';
import { apiError, isVacant, levelChipStyle, levelMeta, placementOf, rolesByUser } from '../../lib/orgStructure';

const filterClass = 'h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700';

const AssignPerson = ({ role, people, roleCounts, onAssigned }) => {
  const [open, setOpen] = useState(false);
  const assign = async (userId) => {
    if (!userId) { setOpen(false); return; }
    try {
      await axiosInstance.put(`/org-positions/${role.id}`, { user_id: userId });
      toast.success('Role filled');
      setOpen(false);
      onAssigned();
    } catch (error) { toast.error(apiError(error, 'Could not assign this person')); }
  };
  return <Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger asChild><button type="button" className="whitespace-nowrap font-semibold text-blue-600 hover:underline">+ Assign person</button></PopoverTrigger>
    <PopoverContent align="start" className="w-80 p-2"><PersonPicker autoFocus people={people} value="" allowVacant={false} roleCounts={roleCounts} onChange={assign} /></PopoverContent>
  </Popover>;
};

// Roles tab: every Org Chart position as a table row.
const RolesTab = ({ positions, people, levels, canEdit, onNewRole, onEditRole, onChanged, limit }) => {
  const [query, setQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const byId = useMemo(() => Object.fromEntries(positions.map((position) => [position.id, position])), [positions]);
  const roleCounts = useMemo(() => Object.fromEntries(Object.entries(rolesByUser(positions)).map(([id, list]) => [id, list.length])), [positions]);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return positions.filter((position) =>
      (!needle || (position.title || '').toLowerCase().includes(needle) || (position.user?.name || '').toLowerCase().includes(needle))
      && (levelFilter === 'all' || String(position.level || 0) === levelFilter)
      && (statusFilter === 'all' || (statusFilter === 'vacant' ? isVacant(position) : statusFilter === 'unplaced' ? placementOf(position) === 'unplaced' : !isVacant(position))))
      .sort((left, right) => (left.level || 0) - (right.level || 0) || (left.title || '').localeCompare(right.title || ''))
      .slice(0, Number(limit));
  }, [positions, query, levelFilter, statusFilter, limit]);

  const vacantCount = positions.filter(isVacant).length;
  const unplacedCount = positions.filter((position) => placementOf(position) === 'unplaced').length;

  return <div data-testid="roles-tab">
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-3">
      <label className="flex h-10 min-w-[220px] max-w-xs flex-1 items-center gap-2 rounded-md border border-slate-200 bg-white px-3">
        <Search size={15} className="text-slate-400" aria-hidden="true" /><span className="sr-only">Search roles</span>
        <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search roles or people" className="h-9 border-0 px-0 shadow-none focus-visible:ring-0" />
      </label>
      <label className="sr-only" htmlFor="roles-level-filter">Level</label>
      <select id="roles-level-filter" value={levelFilter} onChange={(event) => setLevelFilter(event.target.value)} className={filterClass}>
        <option value="all">Level: All</option>
        {levels.map((level) => <option key={level.rank} value={String(level.rank)}>{level.label}</option>)}
      </select>
      <label className="sr-only" htmlFor="roles-status-filter">Status</label>
      <select id="roles-status-filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className={filterClass}>
        <option value="all">Status: All</option><option value="filled">Filled</option><option value="vacant">Vacant</option><option value="unplaced">Not placed</option>
      </select>
      {canEdit && <Button className="ml-auto h-10 bg-blue-600 hover:bg-blue-700" onClick={onNewRole} data-testid="new-role-button"><Plus size={15} className="mr-1.5" />New role</Button>}
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead className="bg-slate-50 text-xs text-slate-500"><tr>
          <th className="px-4 py-3 font-bold">Role</th><th className="px-4 py-3 font-bold">Level</th><th className="px-4 py-3 font-bold">Reports to</th><th className="px-4 py-3 font-bold">Assigned person</th><th className="px-4 py-3 font-bold">Status</th><th className="px-4 py-3"><span className="sr-only">Actions</span></th>
        </tr></thead>
        <tbody>
          {rows.map((position) => {
            const level = levelMeta(levels, position.level);
            const parent = position.parent_id ? byId[position.parent_id] : null;
            const placement = placementOf(position);
            const vacant = isVacant(position);
            return <tr key={position.id} className={`h-[52px] border-t border-slate-100 ${vacant ? 'bg-amber-50/40' : 'bg-white'}`} data-testid={`role-row-${position.id}`}>
              <td className="px-4 font-bold text-slate-800">{position.title}</td>
              <td className="px-4"><span className="whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-bold" style={levelChipStyle(level.color)}>{level.label}</span></td>
              <td className="px-4 text-slate-600">{parent ? parent.title : placement === 'root' ? <span className="text-slate-500">Top of chart</span> : <span className="font-semibold text-amber-700">Not placed</span>}</td>
              <td className="px-4">{!vacant ? (position.user?.name || 'Unknown person') : canEdit ? <AssignPerson role={position} people={people} roleCounts={roleCounts} onAssigned={onChanged} /> : <span className="text-slate-500">Nobody yet</span>}</td>
              <td className={`px-4 font-semibold ${vacant ? 'text-amber-700' : 'text-emerald-700'}`}>{vacant ? 'Vacant' : 'Filled'}</td>
              <td className="px-4 text-right">{canEdit && <button type="button" className="font-semibold text-blue-600 hover:underline" onClick={() => onEditRole(position)} aria-label={`Edit ${position.title}`}>Edit</button>}</td>
            </tr>;
          })}
          {!rows.length && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">{positions.length ? 'No roles match these filters.' : 'No roles yet. Create the first one to start the Org Chart.'}</td></tr>}
        </tbody>
      </table>
    </div>
    <p className="border-t border-slate-200 px-4 py-3 text-sm text-slate-500">{vacantCount} vacant role{vacantCount === 1 ? '' : 's'} · {unplacedCount} role{unplacedCount === 1 ? '' : 's'} not placed on the Org Chart</p>
  </div>;
};

export default RolesTab;
